import { createAsyncThunk } from "@reduxjs/toolkit";
import { SignInResponse } from "../models/SignInResponse";
import { RootState } from "../../store/store";
import axios from "axios";
import { SigninInput } from "../models/SigninInput";
import { PostOkResponse } from "../models/PostOkResponse";
import { CreateUserInput } from "../models/CreateUserInput";
import { User } from "../models/User";
import { v4 } from "uuid";
import { setCredentials, setUser } from "../../store/user-reducer";
import {
  REFRESH_TOKEN_EXPIRED_CODE,
  REFRESH_TOKEN_EXPIRED_MESSAGE,
} from "../../constants/auth";

export const signIn = async (
  dispatch: (action: any) => void,
  input: SigninInput,
): Promise<{ tokens: SignInResponse; user: User }> => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/v1/users/signin",
      {
        email: input.email,
        password: input.password,
      },
      {
        headers: {
          "X-Request-ID": v4(),
        },
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to sign in");
    }

    const userRes = await axios.get(
      process.env.REACT_APP_API_URL + "/v1/users",
      {
        headers: {
          Authorization: response.data.accessToken,
          "X-Request-ID": v4(),
        },
      },
    );

    if (userRes.status !== 200) {
      throw new Error("Failed to get user");
    }

    const result = { tokens: response.data, user: userRes.data };
    dispatch(setCredentials(result.tokens));
    dispatch(setUser(result.user));
    return result;
  } catch (error) {
    console.log("login err = ", error);
    throw error;
  }
};

export const createUser = createAsyncThunk<
  void,
  CreateUserInput,
  { state: RootState }
>("user/createUser", async (input) => {
  const response = await axios.post(
    process.env.REACT_APP_API_URL + "/v1/users",
    input,
    {
      headers: {
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 202) {
    throw new Error("Failed to start email verification");
  }
});

export const confirmUserSignup = async (input: {
  email: string;
  code: string;
}): Promise<User> => {
  const response = await axios.post(
    process.env.REACT_APP_API_URL + "/v1/users/confirmation",
    input,
    { headers: { "X-Request-ID": v4() } },
  );

  if (response.status !== 201) {
    throw new Error("Failed to verify email");
  }

  return response.data as User;
};

export const resendUserSignupConfirmation = async (email: string): Promise<void> => {
  const response = await axios.post(
    process.env.REACT_APP_API_URL + "/v1/users/confirmation/resend",
    { email },
    { headers: { "X-Request-ID": v4() } },
  );

  if (response.status !== 200) {
    throw new Error("Failed to resend email verification code");
  }
};

// TODO: Generate input field for this thunk
export const forgotPassword = createAsyncThunk<
  PostOkResponse,
  string,
  { state: RootState }
>("user/forgotPassword", async (email) => {
  const response = await axios.post(
    process.env.REACT_APP_API_URL + "/v1/users/password/reset",
    {
      email: email,
    },
    {
      headers: {
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to send forgot password email");
  }

  return response.data as PostOkResponse;
});

// TODO: Generate input field for this thunk
export const confirmForgotPassword = createAsyncThunk<
  PostOkResponse,
  { email: string; code: string; password: string },
  { state: RootState }
>("user/confirmForgotPassword", async (input) => {
  const response = await axios.put(
    process.env.REACT_APP_API_URL + "/v1/users/password/reset",
    {
      email: input.email,
      code: input.code,
      newPassword: input.password,
    },
    {
      headers: {
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to confirm forgot password");
  }

  return response.data as PostOkResponse;
});

export const changePassword = createAsyncThunk<
  void,
  { currentPassword: string; newPassword: string },
  { state: RootState }
>("user/changePassword", async (input, { getState }) => {
  const credentials = getState().user.credentials;
  const response = await axios.put(
    process.env.REACT_APP_API_URL + "/v1/users/password",
    {
      oldPassword: input.currentPassword,
      newPassword: input.newPassword,
    },
    {
      headers: {
        Authorization: credentials?.accessToken,
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to change password");
  }
});

export const createCheckoutSession = createAsyncThunk<
  { redirectUrl: string },
  { productCode: string },
  { state: RootState }
>("user/createCheckoutSession", async (input, { getState }) => {
  const credentials = getState().user.credentials;
  const response = await axios.post(
    process.env.REACT_APP_API_URL + "/v1/payments/checkout",
    {
      productCode: input.productCode,
    },
    {
      headers: {
        Authorization: credentials?.accessToken,
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to create checkout session 3");
  }

  return response.data as { redirectUrl: string };
});

export const createGuestCheckoutSession = createAsyncThunk<
  { redirectUrl: string },
  { productCode: string },
  { state: RootState }
>("user/createCheckoutSession", async (input, { getState }) => {
  const credentials = getState().user.credentials;
  const response = await axios.post(
    process.env.REACT_APP_API_URL + "/v1/payments/guest/checkout",
    {
      productCode: input.productCode,
    },
    {
      headers: {
        Authorization: credentials?.accessToken,
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to create checkout session 3");
  }

  return response.data as { redirectUrl: string };
});

export const cancelSubscription = createAsyncThunk<
  void,
  { reason: string; feedback: string },
  { state: RootState }
>("user/cancelSubscription", async (input, { getState }) => {
  const credentials = getState().user.credentials;
  console.log(process.env.REACT_APP_API_URL + "/v1/subscriptions");
  const response = await axios.put(
    process.env.REACT_APP_API_URL + "/v1/subscriptions",
    input,
    {
      headers: {
        Authorization: credentials?.accessToken,
        "X-Request-ID": v4(),
      },
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to cancel subscription");
  }
});

export const getUser = createAsyncThunk<User, void, { state: RootState }>(
  "user/getUser",
  async (_, { getState }) => {
    const credentials = getState().user.credentials;
    const response = await axios.get(
      process.env.REACT_APP_API_URL + "/v1/users",
      {
        headers: {
          Authorization: credentials?.accessToken,
          "X-Request-ID": v4(),
        },
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to get user");
    }

    console.log(
      "window = ",
      window,
      "data layer = ",
      (window as any).dataLayer,
    );

    console.log("pushing data layer event");
    gtag("event", "conversion", {
      send_to: "AW-16667259928/6oIKCLzk4JUaEJjoyIs-",
      user_id: response.data.id,
    });
    console.log("done pushing data layer event");

    return response.data as User;
  },
);

export interface RefreshTokenError {
  code?: number;
  message?: string;
}

export const refreshTokens = createAsyncThunk<
  SignInResponse,
  void,
  { state: RootState; rejectValue: RefreshTokenError }
>("user/refreshTokens", async (_, { getState, rejectWithValue }) => {
  const credentials = getState().user.credentials;
  if (!credentials?.refreshToken) {
    return rejectWithValue({
      code: REFRESH_TOKEN_EXPIRED_CODE,
      message: "missing_refresh_token",
    });
  }

  try {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/v1/users/refresh",
      {
        refreshToken: credentials.refreshToken,
      },
      {
        headers: {
          "X-Request-ID": v4(),
        },
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to refresh tokens");
    }

    return response.data as SignInResponse;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      const responseData = (data || {}) as { code?: number; message?: string };
      if (status === 401 && responseData.code === REFRESH_TOKEN_EXPIRED_CODE) {
        return rejectWithValue({
          code: responseData.code,
          message: responseData.message ?? REFRESH_TOKEN_EXPIRED_MESSAGE,
        });
      }
    }

    throw error;
  }
});

export const deleteAccount = createAsyncThunk<void, void, { state: RootState }>(
  "user/deleteAccount",
  async (_, { getState }) => {
    const credentials = getState().user.credentials;
    const response = await axios.delete(
      process.env.REACT_APP_API_URL + "/v1/users",
      {
        headers: {
          Authorization: credentials?.accessToken,
          "X-Request-ID": v4(),
        },
      },
    );

    if (response.status !== 204) {
      throw new Error("Failed to delete account");
    }
  },
);

export const createDemoAccount = async (
  dispatch: (action: any) => void,
  trackingIds: any,
): Promise<{ tokens: SignInResponse; user: User }> => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "/v1/users/demo",
      {
        trackingIds,
      },
      {
        headers: {
          "X-Request-ID": v4(),
        },
      },
    );

    if (response.status !== 201) {
      throw new Error("Failed to create demo account");
    }

    const result = {
      tokens: {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      },
      user: response.data.user,
    };

    dispatch(setCredentials(result.tokens));
    dispatch(setUser(result.user));
    return result;
  } catch (error) {
    console.log("create demo account error = ", error);
    throw error;
  }
};

export const convertDemoAccount = async (
  dispatch: (action: any) => void,
  input: { name: string; email: string; password: string },
  accessToken: string,
): Promise<{ tokens: SignInResponse; user: User }> => {
  try {
    const response = await axios.put(
      process.env.REACT_APP_API_URL + "/v1/users/demo/convert",
      {
        name: input.name,
        email: input.email,
        password: input.password,
      },
      {
        headers: {
          Authorization: accessToken,
          "X-Request-ID": v4(),
        },
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to convert demo account");
    }

    const result = {
      tokens: {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      },
      user: response.data.user,
    };

    dispatch(setCredentials(result.tokens));
    dispatch(setUser(result.user));
    return result;
  } catch (error) {
    console.log("convert demo account error = ", error);
    throw error;
  }
};
